/**
 * useCameraNavigation
 * Steuert die Potree-Kamera animiert zu einer Annotation-Kameraposition.
 * Potree ist als globales Window-Objekt verfügbar (via <script> Tag).
 */

import { useCallback, useRef } from 'react'
import { AnnotationCamera, Vec3, ViewSnapshot, ClipBoxState } from '../types/annotations'

// ─── Potree Globals ──────────────────────────────────────────────────────────

// THREE.js ist im Potree-Bundle eingebettet, kein separates npm-Paket
interface Vec3Result { x: number; y: number; z: number; project: (camera: any) => Vec3Result }
interface ThreeLike {
  Vector3: new (x: number, y: number, z: number) => Vec3Result
}

declare global {
  interface Window {
    Potree: {
      THREE: ThreeLike
      Viewer: new (element: HTMLElement) => PotreeViewer
    }
  }
}

interface PotreeViewer {
  scene: {
    view: {
      position: { x: number; y: number; z: number }
      getPivot?: () => { x: number; y: number; z: number; toArray: () => number[] }
      lookAt?: (vec: any) => void
    }
    scene: any
    getActiveCamera: () => {
      projectionMatrix: any
      matrixWorldInverse: any
    }
  }
  renderer: {
    domElement: HTMLCanvasElement
    render: (scene: any, camera: any) => void
  }
  pick?: (x: number, y: number) => { position?: { x: number; y: number; z: number } } | null
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

interface UseCameraNavigationOptions {
  /** Referenz auf den Potree-Viewer, der von usePotreeViewer befüllt wird */
  viewerRef: React.RefObject<PotreeViewer | null>
}

interface CameraNavigationReturn {
  /**
   * Fliegt animiert zur Kameraposition der Annotation.
   * @param camera  - Ziel-Kamera (position + target)
   * @param duration - Animationsdauer in ms (Standard: 800)
   */
  flyToCamera: (camera: AnnotationCamera, duration?: number) => void
  /**
   * Setzt die Kamera ohne Animation sofort.
   */
  jumpToCamera: (camera: AnnotationCamera) => void
  /**
   * Projiziert eine 3D-Weltposition auf 2D-Bildschirmkoordinaten.
   * Gibt null zurück, wenn der Punkt hinter der Kamera liegt.
   */
  projectToScreen: (worldPos: Vec3) => { x: number; y: number } | null
}

export function useCameraNavigation({
  viewerRef,
}: UseCameraNavigationOptions): CameraNavigationReturn {
  const animFrameRef = useRef<number | null>(null)

  // ─── Fly-to (animiert) ──────────────────────────────────────────────────

  const flyToCamera = useCallback(
    (camera: AnnotationCamera, duration = 800) => {
      const viewer = viewerRef.current
      if (!viewer) return

      const THREE = window.Potree?.THREE
      if (!THREE) return

      const view = viewer.scene.view
      const pivot = view.getPivot?.() ?? view.position
      const startPos = { x: view.position.x, y: view.position.y, z: view.position.z }
      const startTarget = { x: pivot.x, y: pivot.y, z: pivot.z }

      const endPos = camera.position
      const endTarget = camera.target
      const dur = camera.animationDuration ?? duration

      const startTime = performance.now()

      // Abbreche laufende Animation
      if (animFrameRef.current !== null) {
        cancelAnimationFrame(animFrameRef.current)
      }

      function easeInOut(t: number): number {
        return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
      }

      function lerp(a: number, b: number, t: number): number {
        return a + (b - a) * t
      }

      const v = viewer  // non-null innerhalb der Closure gesichert

      function animate() {
        const elapsed = performance.now() - startTime
        const raw = Math.min(elapsed / dur, 1)
        const t = easeInOut(raw)

        v.scene.view.position.x = lerp(startPos.x, endPos.x, t)
        v.scene.view.position.y = lerp(startPos.y, endPos.y, t)
        v.scene.view.position.z = lerp(startPos.z, endPos.z, t)

        // Potree 2.0: lookAt() statt direktem target-Zugriff
        const THREE = window.Potree?.THREE
        if (THREE) {
          v.scene.view.lookAt?.(new THREE.Vector3(
            lerp(startTarget.x, endTarget.x, t),
            lerp(startTarget.y, endTarget.y, t),
            lerp(startTarget.z, endTarget.z, t),
          ))
        }

        if (raw < 1) {
          animFrameRef.current = requestAnimationFrame(animate)
        } else {
          animFrameRef.current = null
        }
      }

      animFrameRef.current = requestAnimationFrame(animate)
    },
    [viewerRef]
  )

  // ─── Jump-to (ohne Animation) ───────────────────────────────────────────

  const jumpToCamera = useCallback(
    (camera: AnnotationCamera) => {
      const viewer = viewerRef.current
      if (!viewer) return

      viewer.scene.view.position.x = camera.position.x
      viewer.scene.view.position.y = camera.position.y
      viewer.scene.view.position.z = camera.position.z

      // Potree 2.0: lookAt() statt direktem target-Zugriff
      const THREE = window.Potree?.THREE
      if (THREE) {
        viewer.scene.view.lookAt?.(new THREE.Vector3(
          camera.target.x, camera.target.y, camera.target.z
        ))
      }
    },
    [viewerRef]
  )

  // ─── 3D → 2D Projektion ─────────────────────────────────────────────────

  const projectToScreen = useCallback(
    (worldPos: Vec3): { x: number; y: number } | null => {
      const viewer = viewerRef.current
      if (!viewer) return null

      const THREE = window.Potree?.THREE
      if (!THREE) return null

      const canvas = viewer.renderer.domElement
      const camera = viewer.scene.getActiveCamera()
      if (!camera) return null

      // Vector3.project(camera) nutzen — Potree.THREE hat kein Matrix4-Konstruktor
      const vec = new THREE.Vector3(worldPos.x, worldPos.y, worldPos.z)
      vec.project(camera)

      // Hinter der Kamera → nicht rendern
      if (vec.z > 1) return null

      const w = canvas.clientWidth
      const h = canvas.clientHeight

      return {
        x: ((vec.x + 1) / 2) * w,
        y: ((-vec.y + 1) / 2) * h,
      }
    },
    [viewerRef]
  )

  return { flyToCamera, jumpToCamera, projectToScreen }
}

// ─── Utility: Capture current camera from viewer ─────────────────────────────

/**
 * Liest die aktuelle Kameraposition und das Target aus dem Potree-Viewer.
 * Nützlich beim Speichern einer Annotation: "aktuelle Kamera übernehmen".
 */
export function captureCurrentCamera(viewer: PotreeViewer): AnnotationCamera {
  const view = viewer.scene.view
  const pivot = view.getPivot?.() ?? view.position
  return {
    position: {
      x: view.position.x,
      y: view.position.y,
      z: view.position.z,
    },
    target: {
      x: pivot.x,
      y: pivot.y,
      z: pivot.z,
    },
    animationDuration: 800,
  }
}

// ─── Utility: Capture ClipBox state ──────────────────────────────────────────

/**
 * Liest den aktuellen ClipBox-Zustand aus dem Potree-Viewer.
 * Gibt null zurück wenn keine ClipBox aktiv ist.
 */
export function captureCurrentClipBox(): ClipBoxState | null {
  // currentClipVolume ist eine globale Variable in script.js
  const clipVolume = (window as any).currentClipVolume
  if (!clipVolume) return null

  const pos = clipVolume.position
  const scl = clipVolume.scale
  const rot = clipVolume.rotation

  return {
    position: [pos.x, pos.y, pos.z],
    scale:    [scl.x, scl.y, scl.z],
    rotation: [rot.x, rot.y, rot.z],
  }
}

// ─── Utility: Capture full ViewSnapshot ──────────────────────────────────────

/**
 * Erfasst den kompletten Viewer-Zustand: Kamera + ClipBox.
 * Wird im DocumentEditor genutzt, um einen View-Snapshot in den Text einzufügen.
 */
export function captureViewSnapshot(viewer: PotreeViewer, label: string): ViewSnapshot {
  // Canvas-Thumbnail erfassen (klein, 160×100)
  let thumbnail: string | undefined
  try {
    const canvas = viewer.renderer.domElement as HTMLCanvasElement
    // Potree rendert mit preserveDrawingBuffer=false → erst ein Frame rendern
    viewer.renderer.render(viewer.scene.scene, viewer.scene.getActiveCamera())
    // Kleines Thumbnail erstellen
    const thumbCanvas = document.createElement('canvas')
    thumbCanvas.width = 160
    thumbCanvas.height = 100
    const ctx = thumbCanvas.getContext('2d')
    if (ctx) {
      ctx.drawImage(canvas, 0, 0, 160, 100)
      thumbnail = thumbCanvas.toDataURL('image/jpeg', 0.7)
    }
  } catch { /* Canvas-Zugriff fehlgeschlagen */ }

  // Sichtbare Punktwolken erfassen
  const activeCloudIds: string[] = Array.isArray((window as any).activeCloudIds)
    ? [...(window as any).activeCloudIds]
    : []

  return {
    camera: captureCurrentCamera(viewer),
    clipBox: captureCurrentClipBox(),
    activeCloudIds,
    label,
    capturedAt: new Date().toISOString(),
    thumbnail,
  }
}

// ─── Utility: Apply ViewSnapshot to viewer ──────────────────────────────────

/**
 * Stellt einen gespeicherten ViewSnapshot wieder her:
 * Setzt Kamera (animiert) und ClipBox (sofort).
 */
export function applyViewSnapshot(
  viewer: PotreeViewer,
  snapshot: ViewSnapshot,
  flyToCamera: (camera: AnnotationCamera, duration?: number) => void,
) {
  // 1. Kamera animiert ansteuern
  flyToCamera(snapshot.camera, snapshot.camera.animationDuration ?? 600)

  // 2. ClipBox wiederherstellen
  if (snapshot.clipBox) {
    const clipVolume = (window as any).currentClipVolume
    if (clipVolume) {
      const { position, scale, rotation } = snapshot.clipBox
      clipVolume.position.set(position[0], position[1], position[2])
      clipVolume.scale.set(scale[0], scale[1], scale[2])
      clipVolume.rotation.set(rotation[0], rotation[1], rotation[2])
      clipVolume.update()
    }
  }

  // 3. Punktwolken-Sichtbarkeit wiederherstellen
  if (snapshot.activeCloudIds && Array.isArray(snapshot.activeCloudIds)) {
    const win = window as any
    const loadedPCs: Map<string, { pc: { visible: boolean } }> | undefined = win.loadedPointClouds
    const toggleFn: ((id: string) => void) | undefined = win.toggleCloudVisibility
    if (loadedPCs && toggleFn) {
      // Aktuelle sichtbare IDs ermitteln
      const currentActive: string[] = Array.isArray(win.activeCloudIds) ? [...win.activeCloudIds] : []
      // Wolken ausblenden, die im Snapshot nicht aktiv waren
      for (const id of currentActive) {
        if (!snapshot.activeCloudIds.includes(id)) toggleFn(id)
      }
      // Wolken einblenden, die im Snapshot aktiv waren
      for (const id of snapshot.activeCloudIds) {
        const updated: string[] = Array.isArray(win.activeCloudIds) ? win.activeCloudIds : []
        if (!updated.includes(id) && loadedPCs.has(id)) toggleFn(id)
      }
    }
  }
}
